import {
  distinct,
  groupBy,
  naturalSort,
  sortByKey,
} from '@robovision/rvai-utils'
import { storeToRefs } from 'pinia'
import {
  computed,
  type ComputedRef,
} from 'vue'
import { ModelResource } from '~domain/resourcesV2/ModelResource'
import { features } from '~services/features'
import { useProjectsStore } from '../projectsStore/useProjectsStore'
import { useAlgorithmsStore } from '../useAlgorithmsStore'
import { useJobsStore } from '../useJobsStore'
import { useResourcesStore } from '../useResourcesStore'


export function useModels () {
  const resourcesStore = useResourcesStore()
  const {
    getModelResourcesFiltered,
    loadModel,
    loadModels,
  } = resourcesStore
  const {
    getJob,
    loadJob,
    loadJobs,
  } = useJobsStore()
  const { getAlgorithmDefinition } = useAlgorithmsStore()
  const { getProject } = useProjectsStore()

  const { models } = storeToRefs(resourcesStore)

  function getModelResource (resourceId: string): ModelResource | undefined {
    const resource = models.value[resourceId]
    if (!resource || !resource.valid) return undefined
    const job = resource.sourceJobId ? getJob(resource.sourceJobId) : undefined
    return new ModelResource({
      ...resource,
      sourceJob: job,
    })
  }

  function getModels (filters: {definitionId?: string, projectId?: string}): ModelResource[] {
    const modelResources = getModelResourcesFiltered({
      definitionId: filters.definitionId,
      projectId: filters.projectId,
    })

    return modelResources.map(resource => {
      const job = resource.sourceJobId ? getJob(resource.sourceJobId) : undefined
      return new ModelResource({
        ...resource,
        sourceJob: job,
      })
    })
  }

  // Returns all models with the same model definition based on a projectId
  // Introduced for model sharing across projects
  function getAvailableModelsForProject (projectId: string) : ModelResource[] {
    const modelDefinitionIds = getProjectModelDefinitionIds(projectId)

    return modelDefinitionIds.map(modelDefinitionId => getModels({
      definitionId: modelDefinitionId,
      projectId: features.shareModelsAcrossProjects ? undefined : projectId,
    })).flat()
  }

  // Returns an array of the modelDefinition ids of a project to be able to load the available shared models
  function getProjectModelDefinitionIds (projectId: string) : string[] {
    const project = getProject(projectId)
    const algorithmDefinition = getAlgorithmDefinition(project?.defaultAlgorithmId, project?.defaultAlgorithmVersion)
    return (algorithmDefinition?.modelDefinitions ?? []).map(dep => dep.id)
  }

  function getModelResourcesRef (
    projectId?: string,
    definitionId?: string,
  ): ComputedRef<ModelResource[]> {
    return computed<ModelResource[]>(() => {
      const groups = groupBy(Object.values(models.value), 'sourceJobId')
      return [...groups.values()]
        .flatMap(resources => {
          const items = resources.filter(r => (!projectId || r.projectId === projectId) &&
            (!definitionId || r.definitionId === definitionId))

          // all imported models
          if (items.length && items[0]?.sourceJobId === null) {
            return items.map(r => getModelResource(r.id))
          }

          const last = sortByKey(items, 'createdAt', false, naturalSort)[0]
          return last && getModelResource(last.id)
        })
        .filter(v => v?.valid) as ModelResource[]
    })
  }

  async function loadModelResource (
    resourceId: string,
    options: {
      loadJobArtifacts?: boolean
    } = {},
  ): Promise<void> {
    await loadModel(resourceId, true)
    const jobId = models.value[resourceId]?.sourceJobId
    jobId && await loadJob(jobId, {
      allowNotFound: true,
      forceLoad: false,
      loadJobArtifacts: options.loadJobArtifacts,
    })
  }

  // TODO: custom jobs refactoring - we don't need to load all artifact/dependencies. Can be postponed to 5.8.
  async function loadModelResources (
    projectId?: string, // Don't use this method without projectId and with options!
    definitionId?: string,
    options: {
      loadJobArtifacts?: boolean
      loadJobDependencies?: boolean
      loadProjectTags?: boolean,
      loadProjectTagsMetadataLimit?: number
    } = {},
  ): Promise<void> {
    const resourceIds = await loadModels({
      definitionId,
      projectId,
    })
    const jobIds = resourceIds
      .map(id => models.value[id]?.sourceJobId)
      .filter(Boolean)
    await loadJobs(
      {
        jobIds: distinct(jobIds),
        projectId,
      },
      options,
    )
  }

  return {
    getAvailableModelsForProject,
    getModelResource,
    getModelResourcesRef,
    getModels,
    getProjectModelDefinitionIds,
    loadModelResource,
    loadModelResources,
  }
}
